import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FaChevronLeft, FaChevronRight, FaStar, FaUsers, FaCheck } from 'react-icons/fa'
import serviceService from '../services/serviceService'
import Loader from '../components/Loader'
import { formatPrice, getServiceImage, safeJsonParse } from '../utils/helpers'
import SEO from '../utils/SEO'
import { generateServiceSchema, generateBreadcrumbs } from '../utils/structuredData'
import { trackViewItem } from '../utils/analytics'
import Button from '../components/ui/Button'

const ServiceDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [service, setService] = useState(null)
  const [loading, setLoading] = useState(true)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useEffect(() => {
    const loadService = async () => {
      try {
        setLoading(true)
        const data = await serviceService.getServiceById(id)
        setService(data.service)
        trackViewItem({
          id: data.service.id,
          name: data.service.name,
          price: data.service.price,
          category: data.service.type || 'service',
        })
      } catch (error) {
        toast.error('Failed to load service details')
      } finally {
        setLoading(false)
      }
    }

    loadService()
  }, [id])

  const images = useMemo(() => {
    if (!service) return []
    const parsedImages = safeJsonParse(service.images, [])
    const validParsed = Array.isArray(parsedImages) ? parsedImages.filter(Boolean) : []
    if (validParsed.length > 0) return validParsed
    const fallback = getServiceImage(service)
    return fallback ? [fallback] : []
  }, [service])

  const amenities = useMemo(() => {
    if (!service) return []
    const parsedAmenities = safeJsonParse(service.amenities, [])
    return Array.isArray(parsedAmenities) ? parsedAmenities : []
  }, [service])

  const nextImage = () => {
    if (images.length <= 1) return
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  const prevImage = () => {
    if (images.length <= 1) return
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size={80} />
      </div>
    )
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Service not found</h2>
          <Button onClick={() => navigate('/services')}>Browse Services</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      {service && (
        <SEO
          title={`${service.name} - ${service.type || 'Travel Service'}`}
          description={
            service.description
              ? service.description.substring(0, 160)
              : `Book ${service.name} in Himachal Pradesh. Best prices and verified quality.`
          }
          canonical={`/services/${id}`}
          type="product"
          structuredData={[
            generateServiceSchema(service),
            generateBreadcrumbs([
              { name: 'Home', url: '/' },
              { name: 'Services', url: '/services' },
              { name: service.name },
            ]),
          ]}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/services')}
          className="text-primary-600 hover:underline mb-6"
        >
          ← Back to Services
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="relative h-80 md:h-[460px] rounded-2xl overflow-hidden bg-gray-200">
              {images.length > 0 ? (
                <>
                  <img
                    src={images[currentImageIndex]}
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow"
                      >
                        <FaChevronLeft />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow"
                      >
                        <FaChevronRight />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  No image available
                </div>
              )}
            </div>

            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{service.name}</h1>
              <div className="flex flex-wrap gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-semibold uppercase">
                  {service.type}
                </span>
                {service.capacity && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold flex items-center gap-1">
                    <FaUsers /> Capacity: {service.capacity}
                  </span>
                )}
                {service.avg_rating > 0 && (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold flex items-center gap-1">
                    <FaStar /> {service.avg_rating} ({service.review_count || 0})
                  </span>
                )}
              </div>

              <p className="text-gray-700 whitespace-pre-line">
                {service.detailed_description || service.description}
              </p>
            </div>

            {amenities.length > 0 && (
              <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Features</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-700">
                      <FaCheck className="text-green-600" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6 sticky top-24">
              <p className="text-sm text-gray-600 mb-2">Starting from</p>
              <div className="text-3xl font-bold text-primary-600 mb-6">
                {formatPrice(service.base_price)}
                <span className="text-sm font-medium text-gray-600 ml-1">
                  /{service.price_unit?.replace('_', ' ')}
                </span>
              </div>
              <Button fullWidth onClick={() => navigate(`/services/${service.id}/book`)}>
                Book This Service
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServiceDetails
